// @flow
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import prop from 'ramda/es/prop';
import filter from 'ramda/es/filter';
import find from 'ramda/es/find';

import { ECartItemThumb } from 'app/common/layouts/ecart';
import { Table, Tr, TdLabel, Th } from 'app/common/ui-components/table';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import PlainRadioButton from 'app/common/ui-next/plain-radio-button';
import { ModalContent } from 'app/common/components/Modal';
import { EnteringIdentifier, type IdentifierTypeInfo, type IdentifierFormat, type CheckResult } from 'app/common/components-next/identifiers';
import { identifiers as identifiersTypesNames } from 'app/common/constants/identifiers';
import Loading from 'app/common/components/Loader';

import { type OrderItem } from '../../../OrderData';
import styles from './LinkIdentifier.module.scss';;


type Props = {
  loading: boolean,
  personId: number,
  className?: string,
  selectedProductId: number,
  types: Array<IdentifierTypeInfo>,
  availableFormats: Array<IdentifierFormat>,
  orderItems: Array<OrderItem>,
  onCancel: () => void,
  onIdentifierCheck?: (personId: number, identNumber: string) => Promise<CheckResult>,
  onIdentifierSubmit?: (personId: number, {
    type: $Keys<typeof identifiersTypesNames>,
    number: string,
  }) => Promise<boolean>,
  onBeforeIdentifierAdd?: () => Promise<boolean>,
};

const LinkIdentifier = ({
  loading,
  personId,
  className,
  selectedProductId,
  types,
  availableFormats,
  orderItems,
  onCancel,
  onIdentifierCheck,
  onIdentifierSubmit,
  onBeforeIdentifierAdd,
}: Props) => {
  const { t } = useTranslation('shop');

  const orderItemsArranged = useMemo((): Array<OrderItem> => {
    const filtered: Array<OrderItem> = filter(({ id }) => (id !== selectedProductId), orderItems);
    const selected: ?OrderItem = find(({ id }) => (id === selectedProductId), orderItems);
    // первым элементом следует выбранный, если он есть
    return selected ? [selected, ...filtered] : filtered;
  }, [orderItems, selectedProductId]);

  const [locallySelectedProductId, setLocallySelectedProductId] = useState(selectedProductId);
  const handleChangeSelectedProduct = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const { value, checked } = e.currentTarget;
    const targetId = parseInt(value, 10);
    if (checked && find(({ id }) => (id === targetId), orderItems)) {
      setLocallySelectedProductId(targetId);
    }
  }, [orderItems]);

  return (
    <div className={className}>
      <div className={styles.label}>
        {`${t('identifier_add_modal_bought')}:`}
      </div>
      <Table className={styles.table}>
        <thead>
          <Tr hoverHighlighted={false}>
            <Th>&nbsp;</Th>
            <Th alignLeft>{t('identifier_add_modal_name')}</Th>
            <Th alignCenter className={styles.photoColumn}>{t('identifier_add_modal_photo')}</Th>
            <Th alignLeft>{t('identifier_add_modal_cost')}</Th>
          </Tr>
        </thead>
      </Table>
      <ModalContent maxHeight={160} className={styles.tableContent}>
        <Table className={styles.table}>
          <tbody>
            {orderItemsArranged.map(({ id, name, price, image }) => {
              const htmlFor = `product_${String(id)}`;

              return (
                <Tr key={id}>
                  <TdLabel htmlFor={htmlFor} className={styles.radioCell}>
                    <PlainRadioButton
                      id={htmlFor}
                      label={name}
                      hideLabel
                      name="product"
                      value={String(id)}
                      onChange={handleChangeSelectedProduct}
                      checked={locallySelectedProductId === id}
                    />
                  </TdLabel>
                  <TdLabel htmlFor={htmlFor}>
                    {name}
                  </TdLabel>
                  <TdLabel htmlFor={htmlFor}>
                    <ECartItemThumb
                      imgSrc={prop('standart', image)}
                      className={styles.thumb}
                      title={name}
                    />
                  </TdLabel>
                  <TdLabel htmlFor={htmlFor}>
                    {toFixedValueCurrency(price)}
                  </TdLabel>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </ModalContent>
      <div className={styles.label}>
        {`${t('identifier_add_modal_enter_number')}:`}
      </div>
      {loading
        ? <Loading className={styles.loading} />
        : <EnteringIdentifier
          formId="link"
          personId={personId}
          availableFormats={availableFormats}
          types={types}
          onCancel={onCancel}
          onBeforeSubmit={onBeforeIdentifierAdd}
          onIdentifierCheck={onIdentifierCheck}
          onIdentifierSubmit={onIdentifierSubmit}
          hidePrice
        />}
    </div>
  );
};

export default LinkIdentifier;
